import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  clearInviteToken,
  isInviteTokenExpired,
  setInviteToken,
} from '../services/inviteAuth';

/**
 * Magic-link landing page.
 *
 * Reads the invite token from the URL, persists it to sessionStorage, then
 * redirects to the clean portal URL. The token is removed from the visible
 * URL on success so it doesn't leak through screen-shares, browser history,
 * or referer headers on subsequent navigations.
 */
export const InviteEntryPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/onboarding/invite/expired', { replace: true });
      return;
    }
    if (isInviteTokenExpired(token)) {
      clearInviteToken();
      navigate('/onboarding/invite/expired', { replace: true });
      return;
    }
    try {
      setInviteToken(token);
      navigate('/onboarding/invite', { replace: true });
    } catch {
      setHasError(true);
    }
  }, [token, navigate]);

  if (hasError) {
    return (
      <div className="text-center py-16 space-y-2">
        <p className="text-sm text-rgf-gray-700">
          We couldn't open your onboarding link in this browser.
        </p>
        <p className="text-xs text-rgf-gray-500">
          Please make sure private/incognito mode is off and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin text-rgf-primary-600 mx-auto" />
        <p className="text-sm text-rgf-gray-600">Opening your onboarding…</p>
      </div>
    </div>
  );
};
